/*
 * ITERCATUS Reporter — Offline capture queue (Phase 9.2)
 *
 * A persistent IndexedDB queue that survives network loss, tab close/reopen,
 * partial uploads and manual retry. Every item carries a STABLE, IDEMPOTENT
 * client_id so repeated sync attempts NEVER create duplicate Cloudinary
 * assets / media records / notes on the server.
 *
 * Storage discipline:
 *  - After the server confirms an item AND persists metadata, the heavy local
 *    binary is deleted; only a light "done" record is kept briefly.
 *  - Quota is monitored via navigator.storage.estimate(); we warn BEFORE and
 *    DURING pressure and NEVER silently discard already-queued records.
 *  - Retry uses safe exponential backoff (not aggressive infinite retry);
 *    idempotency protects against duplicates on any retry.
 */

const DB_NAME = "itercatus-reporter";
const DB_VERSION = 1;
const STORE = "queue";

let _dbPromise = null;

function openDB() {
  if (_dbPromise) return _dbPromise;
  _dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = () => {
      const db = req.result;
      if (!db.objectStoreNames.contains(STORE)) {
        const os = db.createObjectStore(STORE, { keyPath: "client_id" });
        os.createIndex("state", "state", { unique: false });
        os.createIndex("created_at", "created_at", { unique: false });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror = () => reject(req.error);
  });
  return _dbPromise;
}

export function stableClientId(prefix = "cap") {
  const rnd =
    typeof crypto !== "undefined" && crypto.randomUUID
      ? crypto.randomUUID()
      : Date.now().toString(36) + Math.random().toString(36).slice(2);
  return `${prefix}_${rnd}`;
}

async function tx(mode, fn) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const t = db.transaction(STORE, mode);
    const store = t.objectStore(STORE);
    const res = fn(store);
    t.oncomplete = () => resolve(res && res.result !== undefined ? res.result : res);
    t.onerror = () => reject(t.error);
  });
}

// STATES: SYNC_PENDING -> TRANSCRIPTION_PENDING -> ANALYSIS_PENDING -> DONE
//         (photos/notes go SYNC_PENDING -> DONE)
export async function enqueue(item) {
  const record = {
    client_id: item.client_id || stableClientId(item.kind || "cap"),
    kind: item.kind, // photo | note | impression | voice | location
    payload: item.payload || {},
    blob: item.blob || null, // File/Blob for photo/voice
    state: item.state || "SYNC_PENDING",
    attempts: 0,
    next_attempt_at: 0,
    error: null,
    created_at: Date.now(),
  };
  await tx("readwrite", (s) => s.put(record));
  notify();
  return record.client_id;
}

export async function all() {
  return tx("readonly", (s) => {
    const req = s.getAll();
    return new Promise((resolve) => {
      req.onsuccess = () => resolve(req.result || []);
    });
  });
}

export async function pending() {
  const items = await all();
  return items.filter((i) => i.state !== "DONE");
}

export async function update(client_id, patch) {
  const existing = await tx("readonly", (s) => {
    const req = s.get(client_id);
    return new Promise((resolve) => {
      req.onsuccess = () => resolve(req.result);
    });
  });
  if (!existing) return;
  const merged = { ...existing, ...patch };
  await tx("readwrite", (s) => s.put(merged));
  notify();
  return merged;
}

export async function remove(client_id) {
  await tx("readwrite", (s) => s.delete(client_id));
  notify();
}

// Drop the heavy binary but keep a light DONE record for a short audit window.
export async function markDone(client_id, serverRef) {
  await update(client_id, { state: "DONE", blob: null, server_ref: serverRef, error: null });
}

export async function quota() {
  try {
    if (navigator.storage && navigator.storage.estimate) {
      const { usage, quota } = await navigator.storage.estimate();
      const ratio = quota ? usage / quota : 0;
      return { usage, quota, ratio, pressure: ratio > 0.85 };
    }
  } catch (_e) {}
  return { usage: 0, quota: 0, ratio: 0, pressure: false };
}

// --- simple subscriber model so the UI can react to queue changes ---
const _subs = new Set();
export function subscribe(fn) {
  _subs.add(fn);
  return () => _subs.delete(fn);
}
function notify() {
  _subs.forEach((fn) => {
    try {
      fn();
    } catch (_e) {}
  });
}

// Exponential backoff schedule (safe, capped). attempts -> ms delay.
export function backoffDelay(attempts) {
  const base = 2000;
  const max = 5 * 60 * 1000; // 5 min cap
  return Math.min(max, base * Math.pow(2, Math.max(0, attempts)));
}

export function isReady(item, now = Date.now()) {
  return item.state !== "DONE" && (item.next_attempt_at || 0) <= now;
}
